'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EmergencyVet {
  id: number;
  name: string;
  address: string;
  phone: string;
  distance: string;
  isOpen24Hours: boolean;
  specialties: string[];
  lat: number;
  lng: number;
}

interface VetLocatorMapProps {
  vets: EmergencyVet[];
}

const VetLocatorMap: React.FC<VetLocatorMapProps> = ({ vets }) => {
  const [selectedVet, setSelectedVet] = useState<EmergencyVet | null>(null);

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="grid lg:grid-cols-2 gap-0">
        <div className="h-96 lg:h-[600px] relative overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Emergency Veterinary Clinics Near You"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=40.7128,-74.0060&z=12&output=embed"
            className="border-0"
          />
        </div>

        <div className="p-6 overflow-y-auto max-h-96 lg:max-h-[600px]">
          <div className="mb-6">
            <h3 className="text-2xl font-headline font-bold text-foreground mb-2">
              Emergency Veterinary Clinics
            </h3>
            <p className="text-muted-foreground">
              24/7 emergency care facilities near your location
            </p>
          </div>

          <div className="space-y-4">
            {vets.map((vet) => (
              <div
                key={vet.id}
                onClick={() => setSelectedVet(vet)}
                className={`border rounded-lg p-4 cursor-pointer transition-all duration-300 ${
                  selectedVet?.id === vet.id
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border hover:border-primary/50 hover:shadow-sm'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <h4 className="text-lg font-headline font-semibold text-foreground flex-1">
                    {vet.name}
                  </h4>
                  {vet.isOpen24Hours && (
                    <span className="px-2 py-1 bg-success text-white text-xs font-semibold rounded-full">
                      24/7
                    </span>
                  )}
                </div>

                <div className="space-y-2 mb-3">
                  <div className="flex items-start space-x-2 text-sm text-muted-foreground">
                    <Icon name="MapPinIcon" size={16} variant="outline" className="flex-shrink-0 mt-0.5" />
                    <span>{vet.address}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="PhoneIcon" size={16} variant="outline" className="flex-shrink-0" />
                    <a href={`tel:${vet.phone}`} className="text-primary hover:underline font-semibold">
                      {vet.phone}
                    </a>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <Icon name="MapIcon" size={16} variant="outline" className="flex-shrink-0" />
                    <span>{vet.distance} away</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {vet.specialties.map((specialty, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-foreground text-xs rounded-full"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>

                <div className="mt-4 flex space-x-2">
                  <a
                    href={`tel:${vet.phone}`}
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-error text-white font-semibold rounded-lg hover:bg-error/90 transition-colors"
                  >
                    <Icon name="PhoneIcon" size={16} variant="solid" />
                    <span>Call Now</span>
                  </a>
                  <a
                    href={`https://www.google.com/maps?q=${vet.lat},${vet.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white font-semibold rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="MapIcon" size={16} variant="solid" />
                    <span>Directions</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VetLocatorMap;